/**
 * Dev-mode-only "Plot Matrix (JSON)" tab — `GroundworksReview.tsx` mounts
 * this instead of embedding logic inline. Three data steps, each its own
 * file:
 *
 *  1. `plotMatrixApiSnapshot.ts`  — pull raw groundworks data off the
 *     boq-versions API response (not live tender state).
 *  2. `plotMatrixJsonBuilder.ts`  — turn that into one plain JSON object
 *     (`columns`/`rows`/`columnTotals`/`grandTotal`).
 *  3. `PlotMatrixJsonPlotTable.tsx` / `PlotMatrixJsonStageTable.tsx` —
 *     render a table from that JSON alone, one per view mode.
 *
 * Plus two controls mirroring the live embedded `PlotMatrix.tsx`: a Plot
 * View / Valuation Stage View toggle (`PlotMatrixJsonViewToggle`) and a
 * cell-calculation-strategy switcher (`PlotMatrixJsonCellCalcSwitcher`) that
 * re-runs step 2 with a different formula. `PlotMatrixJsonViewer`
 * additionally exposes the JSON from step 2 directly, so the intermediate
 * artifact is visible, not just implied.
 */
import React, { useMemo, useState } from "react";
import { Empty, Spin } from "antd";
import { ItemLibraryResponse } from "../../../../api/itemLibrary";
import {
  RetainingWallsBuildUpSpecification,
  RetainingWallsBuildUpConfig,
} from "../../../../api/retainingWallsBuildUp";
import { GeoToberWallBuildUpSpecification } from "../../../../api/wallSectionBuildUp";
import { mergePersistedWallBuildUp } from "../../../../utils/wallsBuildUpSave";
import { extractApiGroundworksSnapshot } from "../../../../utils/plotMatrixApiSnapshot";
import { buildPlotMatrixJson } from "../../../../utils/plotMatrixJsonBuilder";
import {
  DEFAULT_CELL_CALC_STRATEGY,
  type CellCalcStrategy,
} from "../../../../utils/plotMatrixCategoryGenerator";
import PlotMatrixJsonSourceBanner from "./PlotMatrixJsonSourceBanner";
import PlotMatrixJsonViewer from "./PlotMatrixJsonViewer";
import PlotMatrixJsonViewToggle, { type PlotMatrixJsonViewMode } from "./PlotMatrixJsonViewToggle";
import PlotMatrixJsonCellCalcSwitcher from "./PlotMatrixJsonCellCalcSwitcher";
import PlotMatrixJsonPlotTable from "./PlotMatrixJsonPlotTable";
import PlotMatrixJsonStageTable from "./PlotMatrixJsonStageTable";

export interface PlotMatrixJsonPanelProps {
  /** `latestVersionDataResolved` from GroundworksReview.tsx — already
   *  resolved to either the latest boq-versions API entry or the version
   *  currently selected in Version History. */
  apiVersionData: unknown;
  isLoadingApiVersionData: boolean;
  isHistoricalVersion: boolean;
  itemLibraryData?: ItemLibraryResponse;
  ohpPercent: number;
  preliminariesPercent: number;
  retainingWallsBuildUp?: {
    specifications?: RetainingWallsBuildUpSpecification[];
    config?: RetainingWallsBuildUpConfig;
  } | null;
  geoToberWallBuildUp?: { specifications?: GeoToberWallBuildUpSpecification[] } | null;
}

const PlotMatrixJsonPanel: React.FC<PlotMatrixJsonPanelProps> = ({
  apiVersionData,
  isLoadingApiVersionData,
  isHistoricalVersion,
  itemLibraryData,
  ohpPercent,
  preliminariesPercent,
  retainingWallsBuildUp,
  geoToberWallBuildUp,
}) => {
  const [viewMode, setViewMode] = useState<PlotMatrixJsonViewMode>("plot");
  const [cellCalcStrategy, setCellCalcStrategy] = useState<CellCalcStrategy>(
    DEFAULT_CELL_CALC_STRATEGY,
  );

  const result = useMemo(() => {
    const snapshot = extractApiGroundworksSnapshot(apiVersionData);
    if (!snapshot) return null;

    const groundworksWithWallDimensions =
      mergePersistedWallBuildUp(snapshot, retainingWallsBuildUp, geoToberWallBuildUp) ?? snapshot;

    return buildPlotMatrixJson({
      groundworksData: groundworksWithWallDimensions,
      itemLibraryData,
      ohpPercent,
      preliminariesPercent,
      includeOhp: ohpPercent > 0,
      includePreliminaries: preliminariesPercent > 0,
      retainingWallsBuildUpSpecs: retainingWallsBuildUp?.specifications,
      geoToberWallBuildUpSpecs: geoToberWallBuildUp?.specifications,
      cellCalcStrategy,
    });
  }, [
    apiVersionData,
    itemLibraryData,
    ohpPercent,
    preliminariesPercent,
    retainingWallsBuildUp,
    geoToberWallBuildUp,
    cellCalcStrategy,
  ]);

  if (isLoadingApiVersionData && !apiVersionData) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin tip="Loading BOQ version data..." />
      </div>
    );
  }

  if (!result || result.rows.length === 0) {
    return (
      <Empty
        description="No plot matrix data available for this BOQ version."
        style={{ margin: "40px 0" }}
      />
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <PlotMatrixJsonSourceBanner
        isHistoricalVersion={isHistoricalVersion}
        generatedAt={result.generatedAt}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <PlotMatrixJsonViewToggle value={viewMode} onChange={setViewMode} />
        <PlotMatrixJsonCellCalcSwitcher value={cellCalcStrategy} onChange={setCellCalcStrategy} />
      </div>

      <PlotMatrixJsonViewer json={result} />

      {viewMode === "plot" ? (
        <PlotMatrixJsonPlotTable
          columns={result.columns}
          rows={result.rows}
          columnTotals={result.columnTotals}
          grandTotal={result.grandTotal}
        />
      ) : (
        <PlotMatrixJsonStageTable columns={result.columns} rows={result.rows} />
      )}
    </div>
  );
};

export default PlotMatrixJsonPanel;
